import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { useAuth } from "../contexts/AuthContext";
import { GetUserBasketResponse, IBasketItem } from "../models/Basket";
import apiConfig from "../utils/apiConfig";

export default function useBaskets(componentName = "useBaskets") {
  const { get, post, postGetFile, put, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const staleTime = 1000 * 60 * 5; //5 minutes

  const getBasket = useQuery({
    queryKey: ["basket", userId],
    queryFn: () => get<GetUserBasketResponse>(`/basket/${userId}`),
    staleTime,
  });

  const checkout = useMutation(
    (body: any) => postGetFile(`/basket/${userId}/checkout`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
        // A basket loaded from a sales quote converts it on checkout
        queryClient.invalidateQueries(["salesQuotes"]);
      },
    },
  );

  const addItem = useMutation(
    (partId: string) => post(`/basket/${userId}/${partId}`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
      },
    },
  );

  const updateItem = useMutation(
    (body: IBasketItem) => put(`/basket/${userId}/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
      },
    },
  );

  const addItemsMultiple = useMutation(
    (partIds: string[]) => post(`/basket/${userId}`, { partIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
      },
    },
  );

  const removeItem = useMutation(
    (partId: string) => del(`/basket/${userId}/${partId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
      },
    },
  );

  const clearBasket = useMutation(
    () => del(`/basket/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["basket", userId]);
      },
    },
  );

  return {
    getBasket,
    checkout,
    addItem,
    updateItem,
    addItemsMultiple,
    removeItem,
    clearBasket,
  };
}
